import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMyOrganization } from '@/hooks/useMyOrganization'
import { useAuthStore } from '@/stores/auth'
import { ErrorState } from '@/components/ui/error-state'
import { Skeleton } from '@/components/ui/loader'
import { UnsavedChangesGuard } from '@/components/ui/unsaved-changes-guard'

/** Only a same-origin app destination can receive the fresh tenant context. */
export function organizationReturnPath(value: unknown): string {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') || /[\\\u0000-\u0020]/.test(value)) return '/dashboard'
  const url = new URL(value, 'https://internal.invalid')
  if (url.origin !== 'https://internal.invalid' || url.pathname.startsWith('/organization/switch')) return '/dashboard'
  return `${url.pathname}${url.search}${url.hash}`
}

/** Browser adapter also lets tests observe the final reload without navigating jsdom. */
export const organizationNavigation = { replace: (path: string) => window.location.replace(path) }

/** Outside AppLayout: no old-tenant readers remain mounted while its pointer moves.
 * Chose the existing route blocker over a second global dirty-state registry.
 * A user who chooses Stay never mounts this route and never calls setActive.
 */
export function OrganizationSwitch() {
  const { orgId } = useParams()
  const { state } = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { loading, error: readError, refetch, setActive } = useMyOrganization()
  const userId = useAuthStore(state => state.user?.id)
  const [error, setError] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const returnTo = organizationReturnPath(state?.returnTo)
  const operation = useRef<{ target: string; promise: Promise<void> } | null>(null)

  useEffect(() => {
    if (loading || readError) return
    if (!orgId) { setError(true); return }
    let current = true
    const target = `${userId}:${orgId}:${attempt}`
    // StrictMode effect replay observes the same request. A retry is explicit.
    if (operation.current?.target !== target) operation.current = { target, promise: setActive(orgId) }
    operation.current.promise.then(() => {
      if (current) setCompleted(true)
    }, () => {
      if (current) setError(true)
    })
    return () => { current = false }
  }, [orgId, userId, loading, readError, attempt, setActive, returnTo])
  // Let the pending guard unmount its beforeunload protection before reloading.
  useEffect(() => { if (completed) organizationNavigation.replace(returnTo) }, [completed, returnTo])

  return <main className="flex min-h-screen items-center justify-center bg-[var(--surface-page)] p-6">
    <UnsavedChangesGuard dirty={false} pending={!error && !readError && !completed} description={t('org.switching')} />
    {error || readError ? <ErrorState description={t('org.switchError')}
      onRetry={() => { if (readError) void refetch(); setError(false); setAttempt(value => value + 1) }}
      secondaryAction={{ label: t('common.back'), onClick: () => navigate(returnTo, { replace: true }) }} />
      : <div role="status" className="w-64 space-y-3"><p>{t('org.switching')}</p><Skeleton className="h-4 w-full" /></div>}
  </main>
}
